/**
 * Hero image mapping: trade slug × terrain → Unsplash photo URL
 *
 * All photo IDs are confirmed working from the existing codebase.
 * Images are served at 1920×1080 with smart crop and WebP auto-format.
 */

// Terrain-branded overlay gradients (CSS background value)
export const TERRAIN_OVERLAYS = {
  "mountain-west":
    "linear-gradient(135deg, rgba(15,25,40,0.88) 0%, rgba(25,50,38,0.68) 55%, rgba(15,25,40,0.50) 100%)",
  "midwest":
    "linear-gradient(135deg, rgba(38,22,12,0.88) 0%, rgba(38,22,12,0.68) 55%, rgba(38,22,12,0.50) 100%)",
  "northeast":
    "linear-gradient(135deg, rgba(18,22,36,0.88) 0%, rgba(46,32,16,0.68) 55%, rgba(18,22,36,0.50) 100%)",
  "south-central":
    "linear-gradient(135deg, rgba(40,18,8,0.88) 0%, rgba(40,18,8,0.68) 55%, rgba(40,18,8,0.50) 100%)",
  "plains":
    "linear-gradient(135deg, rgba(28,32,18,0.88) 0%, rgba(48,38,8,0.68) 55%, rgba(28,32,18,0.50) 100%)",
};

// Terrain-specific taglines shown under the CTA
export const TERRAIN_TAGLINES = {
  "mountain-west":
    "Built for elevation — snow loads, high UV, and year-round outdoor living.",
  "midwest":
    "Heartland craftsmanship. Honest pricing. No-surprise fixed bids.",
  "northeast":
    "New England quality built to survive every season, from ice storms to summer.",
  "south-central":
    "Wind-zone rated, hail-proof construction for the Southern climate.",
  "plains":
    "Big-sky country craftsmanship — built tough for wide-open weather.",
};

// Trade-specific hero taglines
export const TRADE_TAGLINES = {
  decks:
    "Custom outdoor living spaces — composite, hardwood, or pressure-treated — built to last decades.",
  concrete:
    "Precision flatwork that transforms driveways, patios, and pool decks with professional-grade finishes.",
  painting:
    "Professional crews. Premium materials. Results that protect and elevate your home for years.",
  roofing:
    "Expert roofing systems — architectural shingles to standing-seam metal — permits pulled, fixed-bid scope in writing.",
};

/**
 * Returns a full Unsplash image URL for the given trade + terrain combo.
 *
 * Images are strategically assigned so that Mountain West markets get images
 * with crisper, more dramatic light, while Midwest/Plains markets get warm
 * suburban tones. All use confirmed-working photo IDs.
 */

// photo-ID → rough description
// 1600585154340-be6161a56a0c  → Modern craftsman home exterior (sharp, well-lit)
// 1600607687939-ce8a6c25118c  → Beautiful backyard / outdoor living space
// 1600566753190-17f0baa2a6c3  → Patio / elevated outdoor area
// 1600585154526-990dced4db0d  → Classic two-story home, lush yard
// 1512917774080-9991f1c4c750  → Elegant colonial / traditional home exterior
// 1600047509807-ba8f99d2cdde  → Suburban home in green neighborhood
// 1600573472592-401b489a3cdc  → Home with impressive curb appeal
// 1600596542815-ffad4c1539a9  → Luxury home exterior, warm tones

const IMAGE_MAP = {
  // ── DECKS ──
  "decks_mountain-west":  "1600585154340-be6161a56a0c", // dramatic craftsman, mountain feel
  "decks_midwest":        "1600047509807-ba8f99d2cdde", // suburban home, green yard
  "decks_northeast":      "1512917774080-9991f1c4c750", // colonial / traditional home
  "decks_south-central":  "1600596542815-ffad4c1539a9", // luxury warm-tone home
  "decks_plains":         "1600573472592-401b489a3cdc", // open curb appeal

  // ── CONCRETE ──
  "concrete_mountain-west":   "1600566753190-17f0baa2a6c3", // elevated patio / flatwork
  "concrete_midwest":         "1600607687939-ce8a6c25118c", // backyard outdoor space
  "concrete_northeast":       "1600585154526-990dced4db0d", // two-story with paved areas
  "concrete_south-central":   "1600585154340-be6161a56a0c", // craftsman with flatwork
  "concrete_plains":          "1600047509807-ba8f99d2cdde", // suburban driveway setting

  // ── PAINTING ──
  "painting_mountain-west":   "1512917774080-9991f1c4c750", // crisp colonial / craftsman
  "painting_midwest":         "1600585154526-990dced4db0d", // classic painted two-story
  "painting_northeast":       "1600566753190-17f0baa2a6c3", // detailed exterior
  "painting_south-central":   "1600607687939-ce8a6c25118c", // warm outdoor living home
  "painting_plains":          "1600596542815-ffad4c1539a9", // warm luxury exterior

  // ── ROOFING ──
  "roofing_mountain-west":   "1600573472592-401b489a3cdc", // steep-pitch curb appeal
  "roofing_midwest":         "1600596542815-ffad4c1539a9", // strong suburban roof lines
  "roofing_northeast":       "1600047509807-ba8f99d2cdde", // classic New England profile
  "roofing_south-central":   "1600585154526-990dced4db0d", // southern home, prominent roof
  "roofing_plains":          "1600585154340-be6161a56a0c", // plains craftsman, clean roof
};

export function getHeroImage(tradeSlug, terrain) {
  const key = `${tradeSlug}_${terrain}`;
  const id = IMAGE_MAP[key] || IMAGE_MAP["decks_midwest"];
  return `https://images.unsplash.com/photo-${id}?w=1920&h=1080&fit=crop&q=80&auto=format`;
}
